import { LitElement, css} from "lit-element";
import { repeat } from "lit-html/directives/repeat";
import { styleMap } from "lit-html/directives/style-map";
import {classMap} from 'lit-html/directives/class-map.js';
import { html } from "lit-html";
import get from "lodash.get";

/**
 *  Folder Tree Component
 *
 *  @param { Array } nodes - Stores a tree
 *  @param { Number } level - level of current node
 *  @param { Boolean } isRootNode - Determines if the current node is at the root of the tree
 *  @param { String } path - Path to current node
 *  @param { String } idPath - Path to key in tree data structure
 *  @param { String } labelPath - Path to label in tree data structure
 *  @param { String } dataPath - Path to additional data in the tree structure
 *  @param { Boolean } hasDragAndDrop - Enable nodes drag and drop
 *  @param { Object } styleConfig - used to set styling presets such as icon position
 */

class BDSTreeNode extends LitElement {
  static get properties() {
    return {
      nodes: { type: Array },
      isRootNode: { type: Boolean },
      path: { type: Array },
      labelPath: { type: String },
      idPath: { type: String },
      dataPath: { type: String },
      label: { type: String },
      key: { type: String },
      data: { type: Object },
      level: {type: Number },
      isSelected: {type: Boolean},
      hasDragAndDrop: { type: Boolean },
      expandedKeys: { type: Array },
      selectedKeys: { type: Array },
    };
  }

  static get styles() {
    return css`
      li {
        list-style: none;
      }

      div {
        padding: var(--bds-treenode-vertical-padding) 0.5rem;
      }

      .activeClasses {
        border: 1px solid red;
        background: white;
      }
    `;
  }

  constructor() {
    super();
    this.nodes = [];
    this.isRootNode = false;
    this.path = "";
    this.labelPath = "";
    this.idPath = "";
    this.dataPath = "";
    this.label = "";
    this.key = "";
    this.expandedKeys = [];
    this.selectedKeys = [];
    this.dragInactiveHeight = "0.1rem"
    this.dragActiveHeight = "2rem"
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener('mouseover', this.handleMouseOver);
    this.addEventListener('mouseout', this.handleMouseLeave);
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('mouseover', this.handleMouseOver);
    this.removeEventListener('mouseout', this.handleMouseLeave);
  }

  updated(changedProperties) {
    if (changedProperties.has("selectedKeys")) {
      this.isSelected = this.selectedKeys.includes(this.key);
    }
  }

  _hasNodes() {
    if (this.nodes) {
      return this.nodes ? this.nodes.length > 0 : false;
    }
    return false;
  }

  _isExpanded() {
    return this.expandedKeys.includes(this.key);
  }

  _isSelected() {
    return this.selectedKeys.includes(this.key);
  }

  handleDragstart(e) {
    e.dataTransfer.setData("text/plain", this.key);
    e.dataTransfer.dropEffect = "move";
  }

  handleDragover(e) {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    e.target.style.background = "#d8e5f1";
  }

  handleDragleave(e) {
    e.preventDefault();
    e.stopPropagation();
    e.target.style.background = "none";
  }

  handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    let node = e.target.closest("span");
    let dragKey = e.dataTransfer.getData("text/plain");
    let dropKey = node.dataset.key;
    e.target.style.background = "none";
    this._dispatchDragAndDrop(dragKey, dropKey);
  }

  handlePrevDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    e.target.style.background = "#d8e5f1";
    e.target.style.width = "100%";
    e.target.style.height = this.dragActiveHeight;
  }

  handlePrevDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    e.target.style.height = this.dragInactiveHeight;
    e.target.style.background = "none";
  }

  handlePrevDrop(e) {
    let dragKey = e.dataTransfer.getData("text/plain");
    let dropkey = e.target.dataset.key;
    this._dispatchPrevDragAndDrop(dragKey, dropkey);
    e.target.style.background = "none";
    e.target.style.height = this.dragInactiveHeight;
  }

  handleSiblingDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.target.dataset.key !== "ROOT") {
      e.target.style.background = "#d8e5f1";
      e.target.style.width = "100%";
      e.target.style.height = this.dragActiveHeight;
    }
  }

  handleSiblingDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    e.target.style.height = this.dragInactiveHeight;
    e.target.style.background = "none";
  }

  handleSiblingDrop(e) {
    let dragKey = e.dataTransfer.getData("text/plain");
    let dropkey = e.target.dataset.key;
    this._dispatchSiblingDragAndDrop(dragKey, dropkey);
    e.target.style.background = "none";
    e.target.style.height = this.dragInactiveHeight;
  }

  _dispatchDragAndDrop(dragKey, dropKey) {
    let options = {
      detail: {
        dragKey: dragKey,
        dropKey: dropKey,
      },
      bubbles: true,
      composed: true,
    };
    this.dispatchEvent(new CustomEvent("dragAndDrop", options));
  }

  _dispatchSiblingDragAndDrop(dragKey, dropKey) {
    let options = {
      detail: {
        dragKey: dragKey,
        dropKey: dropKey,
      },
      bubbles: true,
      composed: true,
    };
    this.dispatchEvent(new CustomEvent("siblingDragAndDrop", options));
  }

  _dispatchPrevDragAndDrop(dragKey, dropKey) {
    let options = {
      detail: {
        dragKey: dragKey,
        dropKey: dropKey,
      },
      bubbles: true,
      composed: true,
    };
    this.dispatchEvent(new CustomEvent("prevDragAndDrop", options));
  }

  _dispatchNodeSelected() {
    let label = this.label;
    let options = {
      detail: {
        key: this.key,
        hasNodes: this._hasNodes(),
        label: label,
        nodeRef: this,
        selected: this.expandedKeys,
        path: this.path,
        data: { ...this.data },
      },
      bubbles: true,
      composed: true,
    };
    this.dispatchEvent(new CustomEvent("nodeSelected", options));
  }

  _dispatchNodeExpanded(e) {
    e.stopPropagation()
    let label = this.label;
    let options = {
      detail: {
        key: this.key,
        hasNodes: this._hasNodes(),
        label: label,
        nodeRef: this,
        selected: this.expandedKeys,
        path: this.path,
        data: { ...this.data },
      },
      bubbles: true,
      composed: true,
    };
    this.dispatchEvent(new CustomEvent("nodeExpanded", options));
  }

  _appendChildren() {
    if (this._isExpanded()) {
      return html` <style>
          ul {
            padding: 0;
            margin: 0;
          }

          .reorder-2 {
            height: 0.1rem;
            background-color: none;
          }
        </style>
        <ul role="group">
          <div
            data-key=${`${this.key}`}
            class="reorder-2"
            @dragover=${this.handlePrevDragOver}
            @dragleave=${this.handlePrevDragLeave}
            @drop=${this.handlePrevDrop}
          ></div>
          ${repeat(
            this.nodes,
            (node) => get(node, this.idPath),
            (node) => html`<bds-treenode
              key=${get(node, this.idPath)}
              label=${get(node, this.labelPath)}
              .data=${get(node, this.dataPath)}
              .nodes=${node.children}
              .path=${[get(node, this.idPath)]}
              .level=${this.level + 1}
              idPath=${this.idPath}
              ?hasDragAndDrop=${this.hasDragAndDrop}
              labelPath=${this.labelPath}
              dataPath=${this.dataPath}
              .styleConfig=${this.styleConfig}
              .selectedKeys=${this.selectedKeys}
              .expandedKeys=${this.expandedKeys}
            >
            </bds-treenode>`
          )}
        </ul>`;
    }
  }

  _appendArrow() {
    if (this._isExpanded()) {
      return html` <iron-icon
          class="btn"
          @click=${this._dispatchNodeExpanded}
          icon="icons:expand-more"
        ></iron-icon>
        <iron-icon
          class="btn"
          @click=${this._dispatchNodeExpanded}
          icon="vaadin:chevron-down-small"
        ></iron-icon>`;
    }
    return html` <iron-icon
        class="btn"
        @click=${this._dispatchNodeExpanded}
        icon="icons:chevron-right"
      ></iron-icon>
      <iron-icon
        class="btn"
        @click=${this._dispatchNodeExpanded}
        icon="vaadin:chevron-right-small"
      ></iron-icon>`;
  }

  nodeTemplate() {
    const noArrowStyle = {padding: this._hasNodes() ? "0 var(--bds-treenode-vertical-padding)" : "0 0.95rem"}
    return html`
      ${!this.styleConfig?.hasIconBefore ? 
        html`
        <div style="padding-left: 0.5rem">
            ${this.label === "undefined" ? "" : this.label}
        </div>
        ${this._hasNodes() ? this._appendArrow() : null}
        `
        : html`
        ${this._hasNodes() ? this._appendArrow() : null}
        <div style=${styleMap(noArrowStyle)}>
            ${this.label === "undefined" ? "" : this.label}
        </div>
        `}
    `
  }

  render() {
    const spanStyle = { 
      // padding: `0.25rem 0.5rem 0.25rem ${this.level * 0.5}rem`, 
              padding: `var(--bds-treenode-vertical-padding) 0.5rem var(--bds-treenode-vertical-padding) ${this.level * 0.5}rem`,
              background: this._isSelected() ? "var(--bds-treenode-active, #a6bcc6)" : null
              }                        
    return html`
      <style>
        li {
          text-indent: 0;
          margin: 0;
          cursor: pointer;
        }
        
        iron-icon {
          width: fit-content;
          max-width: 1rem;
          margin-right: 0.01rem;
        }

        .btn {
          position: relative;
          z-index: 1000;
        }

        .btn:hover {
          color: var(--bds-treenode-arrow-hover, lightblue);
        }

        span:hover {
          background-color: var(--bds-treenode-hover);
        }
       
        .node-container {
          display: flex;
          align-items: center;
          justify-content: ${`${this.styleConfig?.hasIconBefore ? "flex-start" : "space-between"}`} ;
          padding-left: ${`${this.level * 0.5}rem`}
        }

        .reorder {
          height: 0.1rem;
          background-color: none;
        }
      </style>
      <li
        role="treeitem"
        part=${this.isRootNode ? "root" : "node"}
        aria-expanded="false"
      >
        <span
          class="node-container"
          part="node-container"
          style=${styleMap(spanStyle)}
          data-key=${this.key}
          draggable=${Boolean(this.hasDragAndDrop)}
          @dragover=${this.handleDragover}
          @dragleave=${this.handleDragleave}
          @drop=${this.handleDrop}
          @dragstart=${this.handleDragstart}
          @click=${this._dispatchNodeSelected}
        >
          ${this.nodeTemplate()}
        </span>
        ${this._appendChildren()}
      </li>
      <div
        data-key=${`${this.key}`}
        class="reorder"
        @dragover=${this.handleSiblingDragOver}
        @dragleave=${this.handleSiblingDragLeave}
        @drop=${this.handleSiblingDrop}
      ></div>
    `;
  }

  createRenderRoot() {
    return this;
  }
}

customElements.define("bds-treenode", BDSTreeNode);
